import React, { useState, useEffect } from 'react';
import CommonModalForm from '../../components/commonComponent/CommonModalForm';
import CommonTable from '../../components/commonComponent/CommanTable';
import { validateForm } from '../../utils/formUtils';
import { toastSuccess, toastError, confirmDelete } from '../../utils/alertHelper';
import axios from '../../utils/axiosInstance';
import { FaEdit, FaTrash } from 'react-icons/fa';

const Currancy = () => {
  const [currencies, setCurrencies] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formValues, setFormValues] = useState({
    name: '',
    symbol: '', 
    code: '',
  });
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');  

  useEffect(() => {
    fetchCurrencies();
  }, []);

  const fetchCurrencies = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/admin/api/currency');
      setCurrencies(res.data.currencies || []);
    } catch (err) {
      console.error('Failed to fetch Currencies:', err);
      toastError('Failed to fetch currencies');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const validationErrors = validateForm(formValues, {
      name: { required: true },
      symbol: { required: true },
      code: { required: true },
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setSaving(true);
      const payload = {
        name: formValues.name,
        symbol: formValues.symbol,
        code: formValues.code,
      };

      if (editingId) {
        await axios.put(`/admin/api/currency/${editingId}`, payload);
        toastSuccess('Currency updated successfully!');
      } else {
        await axios.post('/admin/api/currency', payload);
        toastSuccess('Currency added successfully!');
      }
      fetchCurrencies();
      handleCancel();
    } catch (err) {
      console.error('Failed to save Currency:', err);
      toastError(err.response?.data?.message || 'Failed to save Currency');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (currency) => {
    setFormValues({
      name: currency.name || '',
      symbol: currency.symbol || '',
      code: currency.code || '',
    });
    setEditingId(currency._id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      const res = await confirmDelete('Are you sure you want to delete this currency?');
      if (!res.isConfirmed) return;
    } catch (err) { return; }
    try {
      await axios.delete(`/admin/api/currency/${id}`);
      toastSuccess('Currency deleted successfully!');
      fetchCurrencies();
    } catch (err) {
      console.error('Failed to delete Currency:', err);
      toastError('Failed to delete Currency');
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setFormValues({ name: '', symbol: '', code: '' });
    setEditingId(null);
    setErrors({});
  };

  // Define table columns for CommonTable
  const tableColumns = [
    { key: 'srNo', label: 'Sr no.' },
    { key: 'name', label: 'Name' },
    { key: 'symbol', label: 'Symbol' },
    { key: 'code', label: 'Code' },
    { key: 'actions', label: 'Actions' }
  ];

  // Prepare data for CommonTable
  const filteredCurrencies = currencies.filter(currency => 
    currency.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    currency.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const tableData = filteredCurrencies.map((currency, idx) => ({
    srNo: idx + 1,
    name: currency.name,
    symbol: currency.symbol,
    code: currency.code,
    actions: (
      <div>
        <button
          className="btn btn-sm me-1 py-0 px-2 fs-6 lh-sm shadow-none border-0 text-primary-custom"
          title="Edit"
          onClick={() => handleEdit(currency)}
        >
          <FaEdit size={14} />
        </button>
        <button
          className="btn btn-sm py-0 px-2 fs-6 lh-sm shadow-none border-0 text-danger"
          title="Delete"
          onClick={() => handleDelete(currency._id)}
        >
          <FaTrash size={14} />
        </button>
      </div>
    )
  }));

  return (
    <div className="container mt-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Currency</h3>
        <div className="d-flex gap-2">
          <input
            type="text"
            className="form-control"
            placeholder="Search Currencies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: '250px' }}
          />
          <button
            className="btn btn-primary"
            onClick={() => setShowModal(true)}
          >
            + Add New Currency
          </button>
        </div>
      </div>

      {loading ? (
        <div className="card">
          <div className="card-body">
            <div className="text-center py-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <CommonTable
          columns={tableColumns}
          data={tableData}
        />
      )}
      <CommonModalForm
        visible={showModal}
        title={editingId ? 'Edit Currency' : 'Add Currency'}
        fields={[
          { 
            name: 'name', 
            label: 'Currency Name', 
            type: 'text',
            placeholder: 'Enter Currency Name'
          },
          { 
            name: 'symbol', 
            label: 'Symbol', 
            type: 'text',
            placeholder: 'Enter Symbol (e.g., $, €, ₹)'
          },
          { 
            name: 'code', 
            label: 'Currency Code', 
            type: 'text',
            placeholder: 'Enter Currency Code (e.g., USD, EUR, INR)'
          },
        ]}
        values={formValues}
        errors={errors}
        onChange={setFormValues}
        onSubmit={handleSave}
        onCancel={handleCancel}
        submitLabel={saving ? (editingId ? 'Updating...' : 'Saving...') : (editingId ? 'Update' : 'Save')}
        requiredFields={['name', 'symbol', 'code']}
        disabled={saving}
      />
    </div>
  );
};

export default Currancy;
